import React from 'react';

import type { UserDto } from '../api/types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchUsers } from '../store/thunks/fetchUsers';

export type UseUserByIdResult = {
  user: UserDto | null;
  isLoading: boolean;
};

// Global cache to track IDs that have been checked across all hook instances
const checkedIdsGlobal = new Set<string>();
const loggedIdsGlobal = new Set<string>();
let fetchPromise: Promise<void> | null = null;

/**
 * Hook that finds a user by telegramId in the store.
 * If user is not found, fetches users from server and checks again.
 * If still not found after fetch, returns null and logs a warning (once per ID).
 *
 * @param telegramId - The Telegram ID of the user to find
 * @returns Object with user data (or null) and loading state
 */
export function useUserById(telegramId: string | undefined): UseUserByIdResult {
  const dispatch = useAppDispatch();
  const users = useAppSelector((s) => s.entities.users.items);

  const [isLoading, setIsLoading] = React.useState(false);

  const user = React.useMemo(() => {
    if (!telegramId) return null;
    return users.find((u) => u.telegramId === telegramId) ?? null;
  }, [users, telegramId]);

  React.useEffect(() => {
    if (!telegramId) return;
    if (user) return;
    if (checkedIdsGlobal.has(telegramId)) return;

    let cancelled = false;

    const fetchAndCheck = async () => {
      setIsLoading(true);

      // Reuse existing fetch promise to avoid duplicate requests
      if (!fetchPromise) {
        fetchPromise = dispatch(fetchUsers())
          .unwrap()
          .catch((error) => {
            console.error('[useUserById] Failed to fetch users:', error);
          })
          .finally(() => {
            fetchPromise = null;
          });
      }

      await fetchPromise;

      if (!cancelled) {
        setIsLoading(false);
        checkedIdsGlobal.add(telegramId);
      }
    };

    void fetchAndCheck();

    return () => {
      cancelled = true;
    };
  }, [telegramId, user, dispatch]);

  // Log warning once if user not found after fetch attempt
  React.useEffect(() => {
    if (!telegramId) return;
    if (isLoading) return;
    if (user) return;
    if (!checkedIdsGlobal.has(telegramId)) return;
    if (loggedIdsGlobal.has(telegramId)) return;

    loggedIdsGlobal.add(telegramId);
    console.warn(`[useUserById] User with telegramId "${telegramId}" not found after fetch`);
  }, [telegramId, user, isLoading]);

  return { user, isLoading };
}
